// Dialog „Lesezeichen setzen" (Nachrichtenmenü). Vertrag: docs/API.md 3.11.
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../../../lib/api';
import { bookmarksApi } from '../api';
import type { ExtraContext } from '../index';
import { useStableHandler } from '../scheduled/hooks';
import { toast } from '../toast';
import { Button, Dialog, ErrorBox, Field } from '../ui';
import { FolderColorPicker } from './FolderColorPicker';
import { BOOKMARK_FOLDERS_KEY, BOOKMARKS_KEY, FOLDER_NAME_MAX, sortFolders } from './keys';
import './bookmarks.css';

const NEW_FOLDER = '__new';

export function BookmarkDialog({ messageId, onClose: onCloseProp }: ExtraContext) {
  const onClose = useStableHandler(onCloseProp);
  const qc = useQueryClient();
  const noteMax = FIXED_LIMITS.bookmarkNoteMax;
  const folders = useQuery({ queryKey: BOOKMARK_FOLDERS_KEY, queryFn: bookmarksApi.folders });
  const [folder, setFolder] = useState('');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);

  const save = useMutation({
    mutationFn: async (input: { messageId: string; folderId?: string; newFolder?: { name: string; color: string | null }; note?: string }) => {
      let folderId = input.folderId;
      if (input.newFolder) {
        const created = await bookmarksApi.createFolder({ name: input.newFolder.name, ...(input.newFolder.color ? { color: input.newFolder.color } : {}) });
        folderId = created.id;
        setFolder(created.id);
      }
      return bookmarksApi.create({ messageId: input.messageId, ...(folderId ? { folderId } : {}), ...(input.note ? { note: input.note } : {}) });
    },
    onSuccess: () => {
      toast({ kind: 'success', title: 'Lesezeichen gespeichert', href: '/bookmarks' });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) setExists(true);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: BOOKMARKS_KEY }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setExists(false);
    if (!messageId) return setError('Keine Nachricht ausgewählt.');
    if (note.length > noteMax) return setError(`Notiz: höchstens ${noteMax} Zeichen.`);
    let newFolder: { name: string; color: string | null } | undefined;
    if (folder === NEW_FOLDER) {
      const name = newName.trim();
      if (!name) return setError('Bitte einen Ordnernamen eingeben.');
      if (name.length > FOLDER_NAME_MAX) return setError(`Ordnername: höchstens ${FOLDER_NAME_MAX} Zeichen.`);
      newFolder = { name, color: newColor };
    }
    setError(null);
    save.mutate({ messageId, folderId: folder && folder !== NEW_FOLDER ? folder : undefined, newFolder, note: note.trim() || undefined });
  };

  return (
    <Dialog
      title="Lesezeichen setzen"
      testId="bookmarks-dialog"
      onClose={onClose}
      footer={
        <>
          <Button testId="bookmarks-dialog-cancel" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" type="submit" form="lfx-bookmark-form" testId="bookmarks-dialog-submit" disabled={save.isPending}>
            {save.isPending ? 'Speichere …' : 'Speichern'}
          </Button>
        </>
      }
    >
      <form id="lfx-bookmark-form" className="lfx-stack" onSubmit={submit} noValidate>
        <Field label="Ordner" hint={folders.isError ? 'Ordner konnten nicht geladen werden – speichern ohne Ordner ist möglich.' : undefined}>
          {(id) => (
            <select id={id} className="lfx-select" data-testid="bookmarks-dialog-folder" value={folder} onChange={(e) => setFolder(e.target.value)}>
              <option value="">Ohne Ordner</option>
              {sortFolders(folders.data ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
              <option value={NEW_FOLDER}>+ Neuer Ordner …</option>
            </select>
          )}
        </Field>
        {folder === NEW_FOLDER && (
          <div className="lfx-stack lfx-bm-newfolder" data-testid="bookmarks-dialog-new-folder">
            <Field label="Name des neuen Ordners">
              {(id) => (
                <input id={id} className="lfx-input" data-testid="bookmarks-dialog-folder-name" maxLength={FOLDER_NAME_MAX} value={newName} onChange={(e) => setNewName(e.target.value)} />
              )}
            </Field>
            <FolderColorPicker value={newColor} onChange={setNewColor} testId="bookmarks-dialog-color" />
          </div>
        )}
        <Field
          label="Notiz (optional)"
          hint={
            <span className={note.length > noteMax ? 'lfx-bm-count lfx-bm-count--over' : 'lfx-bm-count'} data-testid="bookmarks-dialog-count">
              {note.length} / {noteMax}
            </span>
          }
        >
          {(id) => <textarea id={id} className="lfx-textarea" data-testid="bookmarks-dialog-note" value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        {error && (
          <div className="lfx-field__error" role="alert" data-testid="bookmarks-dialog-validation">
            {error}
          </div>
        )}
        {exists ? (
          <div className="lfx-bm-exists" role="status" data-testid="bookmarks-dialog-exists">
            Bereits gespeichert.{' '}
            <Link to="/bookmarks" className="lfx-link" data-testid="bookmarks-dialog-exists-link" onClick={onClose}>
              Zu den Lesezeichen
            </Link>
          </div>
        ) : (
          <ErrorBox error={save.error} testId="bookmarks-dialog-error" />
        )}
      </form>
    </Dialog>
  );
}
